import "./importCsvModal.scss";

import { fromJS, List, Map } from "immutable";
import { delay, isNil, trim } from "lodash";
import Papa from "papaparse";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import FormButton from "../../components/form/FormButton";
import FormCheckbox from "../../components/form/FormCheckbox";
import FormText from "../../components/form/FormText";
import ModalFormDropdown from "../../components/form/ModalFormDropdown";
import ModalBody from "../../components/modal/ModalBody";
import ModalCheckboxGrid from "../../components/modal/ModalCheckboxGrid";
import ModalField from "../../components/modal/ModalField";
import ModalFooter from "../../components/modal/ModalFooter";
import ModalFooterLeftGroup from "../../components/modal/ModalFooterLeftGroup";
import ModalFooterLoader from "../../components/modal/ModalFooterLoader";
import ModalFooterRightGroup from "../../components/modal/ModalFooterRightGroup";
import ModalHeading from "../../components/modal/ModalHeading";
import ModalRow from "../../components/modal/ModalRow";
import ModalView from "../../components/modal/ModalView";
import PageSlider from "../../components/pageSlider";
import { csvImportDateOrderOptions, DateOrderIdentifiers } from "../../constants/dateOrders";
import {
  CAN_IMPORT_FIELD,
  CSV_DELIMITER,
  CSV_IMPORT_ADD_TO_NARRATIVE_CHECK_BOX_LABEL,
  CSV_IMPORT_ADD_TO_NARRATIVE_TOOLTIP,
  CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT,
  CSV_IMPORT_DATE_FORMAT_CHECK_BOX_LABEL,
  CSV_IMPORT_DATE_FORMAT_CHECK_BOX_TOOLTIP,
  CSV_IMPORT_DATE_FORMAT_LABEL,
  CSV_IMPORT_DATE_FORMAT_TOOLTIP,
  CSV_IMPORT_DEFAULT_ITEM_TYPE_DESCRIPTION,
  CSV_IMPORT_DEFAULT_ITEM_TYPE_LABEL,
  CSV_IMPORT_DEFAULT_ITEM_TYPE_TOOLTIP,
  CSV_IMPORT_HEADER_ROW_CHECK_BOX_LABEL,
  CSV_IMPORT_HEADER_ROW_TOOLTIP,
  CSV_IMPORT_PREVIEW_TABLE_LABEL,
  CSV_IMPORT_SETTINGS_LABEL,
  CSV_IMPORT_SHOW_LOADING_ITEM_NUM
} from "../../constants/importExportFile";
import interfaceMessages from "../../constants/interfaceMessages";
import PubSubTopics from "../../constants/pubSubTopics";
import sliderTransitions from "../../constants/sliderTransitions";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import dialogs from "../../dialogs";
import batchOperations from "../../modules/batchOperations";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { colorSelectors } from "../../modules/definition/colors";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import { narrativeSelectors } from "../../modules/narrative";
import { preferencesSelectors } from "../../modules/preferences";
import { uiStateOperations } from "../../modules/uiState";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import ImportCsvPreviewTable from "./importCsvPreviewTable";
import selectors from "./selectors";
import { clearItemValues, createImportActions, getCsvImportDataOnly, updateItemsValues } from "./utils";

const PREVIEW_PAGE = "PREVIEW_PAGE";
const SUMMARY_PAGE = "SUMMARY_PAGE";

const STEPS = [PREVIEW_PAGE, SUMMARY_PAGE];

const MODAL_TITLE = {
  [PREVIEW_PAGE]: "Choose Columns",
  [SUMMARY_PAGE]: "Confirm Data"
};

const RIGHT_BUTTON_LABEL = {
  [PREVIEW_PAGE]: "Next: Confirm Data",
  [SUMMARY_PAGE]: "Import"
};

const maxRows = 8;

const getImportItemCount = countsMap => countsMap.reduce((sum, num) => (sum += num), 0);

const getSummaryLine = (text, key) => <li key={key}>{text}</li>;

const getTypeLabel = (type, typesById) => {
  return typeSelectors.getTypeLabel(typesById.get(type)).toLowerCase();
};

const fixItemTypes = (itemsValues, defaultType) => itemsValues.map(i => i.update("type", t => (t ? t : defaultType)));

const getTypeOptions = (types, disableNarrativeOnly) =>
  types.map(t => ({
    label: t.label,
    value: t.id,
    disabled: disableNarrativeOnly && typeSelectors.isNarrativeOnlyType(t)
  }));

const getColumnCount = (rawData, hasHeaderRow) => {
  return rawData.count() > 0
    ? hasHeaderRow
      ? rawData.get(0).count()
      : rawData.reduce((max, row) => Math.max(max, row.count()), 0)
    : 0;
};

const initColumnTypes = (parsedData, hasHeaderRow, columnTypeOptions) => {
  const columnCount = parsedData ? getColumnCount(parsedData, hasHeaderRow) : 0;
  // Default to blank for everything
  let types = List(Array(columnCount).fill(CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT));
  let matchedFields = {};

  // And then go through the data and auto-select columns where the labels match
  if (parsedData && hasHeaderRow) {
    const headerRow = parsedData.get(0);
    types = types.map((initValue, index) => {
      const headerLabel = headerRow.get(index);
      const matchingOpt = columnTypeOptions.find(
        obj =>
          headerLabel &&
          !matchedFields[obj.value] &&
          !matchedFields[headerLabel] &&
          obj.matchLabels.find(l => l.toLowerCase() === headerLabel.toLowerCase())
      );
      const matchValue = matchingOpt ? matchingOpt.value : null;
      if (matchValue) {
        matchedFields[matchValue] = true;
        matchedFields[headerLabel] = true;
      }
      const finalValue = matchValue ? matchValue : initValue;
      return finalValue;
    });
  }
  return types;
};

const initColumnMatchCounts = columnTypeOptions => {
  return columnTypeOptions.reduce(
    (errorMap, opt) => errorMap.set(opt.value, Map({ matched: 0, misMatched: 0 })),
    Map()
  );
};

const initItemsValues = (rawData, hasHeaderRow, defaultItemType) => {
  return getCsvImportDataOnly(rawData, hasHeaderRow).map(_row => Map({ type: defaultItemType }));
};

const getInitStatesOfData = (
  parsedData,
  hasHeaderRow,
  columnTypeOptions,
  delimiter = CSV_DELIMITER.COMMA,
  errorCount = 0
) => {
  const columnCount = getColumnCount(parsedData, hasHeaderRow);
  return {
    parsedData,
    hasHeaderRow: hasHeaderRow,
    columnCount: columnCount,
    delimiter: delimiter,
    csvErrorCount: errorCount,
    columnTypes: initColumnTypes(parsedData, hasHeaderRow, columnTypeOptions),
    columnMatchCounts: initColumnMatchCounts(columnTypeOptions),
    isLoading: false
  };
};

class ImportCsvModal extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      stepHistory: List([PREVIEW_PAGE]),
      parsedData: List(),
      columnCount: 0,
      isImporting: false,
      isLoading: true,
      csvErrorCount: null,
      delimiter: CSV_DELIMITER.COMMA,
      hasHeaderRow: true,
      addToNarrative: false,
      defaultItemType: props.defaultItemType,
      twoDigitsToTwoThousandYear: false,
      dateOrder: DateOrderIdentifiers.DayMonthYear,
      columnTypes: initColumnTypes(null),
      // Relevant options for each column type (Eg. duration default precision)
      columnInfoOptions: selectors.initColumnInfoOptions(props.typesById, props.referencesById),
      columnMatchCounts: initColumnMatchCounts(props.columnTypeOptions),
      containerRectHeight: null,
      tableHeight: null
    };

    this.onDataReceived = this.onDataReceived.bind(this);
    this.setHasHeaderRow = this.setHasHeaderRow.bind(this);
    this.setAddToNarrative = this.setAddToNarrative.bind(this);
    this.setDefaultItemType = this.setDefaultItemType.bind(this);
    this.setDateOrder = this.setDateOrder.bind(this);
    this.setTwoDigitsToTwoThousandYear = this.setTwoDigitsToTwoThousandYear.bind(this);
    this.setColumnType = this.setColumnType.bind(this);
    this.updateColumnInfoOptions = this.updateColumnInfoOptions.bind(this);
    this.onBackStep = this.onBackStep.bind(this);
    this.onNextStep = this.onNextStep.bind(this);
    this.onPreviewFinish = this.onPreviewFinish.bind(this);
    this.onImport = this.onImport.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.resetAllColumns = this.resetAllColumns.bind(this);
    this.performImport = this.performImport.bind(this);
    this._itemsValues = initItemsValues(this.state.parsedData, this.state.hasHeaderRow, this.state.defaultItemType);
    this._relItemsValues = List();
    this._storedMatchCounts = Map();

    this._resetAllColumnsOnNextUpdate = false;
    this._performImportOnNextUpdate = false;

    this.onChangeTableHeight = this.onChangeTableHeight.bind(this);
    this.updateContainerHeight = this.updateContainerHeight.bind(this);
    this.tableContainerRef = React.createRef();
  }

  componentDidMount() {
    this._pubSubBrowseToken = PubSub.subscribe(PubSubTopics.importCsvDataReceived, this.onDataReceived);
    PubSub.publish(PubSubTopics.importCsvModalReady, null);
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.IMPORT_CSV_OPENED
    });
    this._pubSubEscapeKeyDownToken = PubSub.subscribe(PubSubTopics.escapeKeyDown, this.onCloseClick);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this._performImportOnNextUpdate) {
      this._performImportOnNextUpdate = false;
      // Need to do this on delay so the visual update can go through first
      delay(this.performImport, 50);
      return;
    }
    if (prevState.hasHeaderRow !== this.state.hasHeaderRow || this._resetAllColumnsOnNextUpdate) {
      // Recalculate all selected columns
      this.resetAllColumns();
    } else {
      if (prevState.defaultItemType !== this.state.defaultItemType) {
        this.updateItemsValuesByColumnType("type");
      }
      if (
        prevState.twoDigitsToTwoThousandYear !== this.state.twoDigitsToTwoThousandYear ||
        prevState.dateOrder !== this.state.dateOrder
      ) {
        this.reParseDateValues();
      }
      if (prevState.addToNarrative !== this.state.addToNarrative) {
        this.updateItemsValuesByColumnType("type");
        this.reParseRelationshipColumns();
      }
    }

    if (this._storedMatchCounts.count() > 0) {
      this.applyMatchCounts();
    }

    this.updateContainerHeight();
  }

  componentWillUnmount() {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.IMPORT_CSV_CLOSED
    });
    PubSub.unsubscribe(this._pubSubBrowseToken);
    PubSub.unsubscribe(this._pubSubEscapeKeyDownToken);
  }

  onDataReceived(msg, data) {
    // This function uses Papa Parse to convert the CSV
    // https://www.papaparse.com
    // Results format:
    //    results = {
    //      data: [ ... ],    // parsed data
    //      errors: [ ... ],  // errors encountered
    //      meta: {	... }     // extra parse info
    //    }
    // Access delimiter at:
    //    results.meta.delimiter
    // Calling the function to run on a web worker in case it is
    // a really huge file. That way the interface will lock up less often.

    // Do not need to supply delimiter... it will work it out for us
    // Set to skip blank lines
    const csvResult = Papa.parse(data, {
      skipEmptyLines: true,
      transform: s => trim(s)
    });

    const parsedData = fromJS(csvResult.data);

    this._resetAllColumnsOnNextUpdate = true;

    this.setState(
      getInitStatesOfData(
        parsedData,
        this.state.hasHeaderRow,
        this.props.columnTypeOptions,
        csvResult.meta.delimiter,
        csvResult.errors.length
      )
    );

    // Note: I have removed this as it was overriding the changes that were triggered
    // by resetAllColumns() below, which was added to assist with auto-selecting columns based
    // on matching labels...
    //
    // I have seen no ill-effects from its removal
    //
    // this.resetImportData(parsedData, this.state.hasHeaderRow);
  }

  resetAllColumns() {
    this._resetAllColumnsOnNextUpdate = false;
    this.state.columnTypes.forEach(columnType => {
      this.updateItemsValuesByColumnType(columnType);
    });
    this.reParseRelationshipColumns();
    this.applyMatchCounts();
  }

  onBackStep() {
    this.setState({ stepHistory: this.state.stepHistory.pop() });
  }

  onNextStep(stepId) {
    const history = this.state.stepHistory;
    this.setState({
      stepHistory: history.push(stepId)
    });
  }

  resetImportData(parsedData, hasHeaderRow) {
    this._itemsValues = initItemsValues(parsedData, hasHeaderRow, this.state.defaultItemType);
    this._relItemsValues = List();
  }

  setHasHeaderRow(value) {
    // Initialize column types, need to re-count and re-detecting
    const { hasHeaderRow } = this.state;
    if (value !== hasHeaderRow) {
      this.setState({ hasHeaderRow: value });
      // Should remove the first row of data to match
      // the current date with header row.
      // Otherwise, the parsed data would have infant row which was the header one
      if (value) {
        this._itemsValues = this._itemsValues.slice(1);
      }
    }
  }

  setAddToNarrative(value) {
    const { addToNarrative } = this.state;
    if (value !== addToNarrative) {
      this.setState({ addToNarrative: value });
    }
  }

  reParseDateValues() {
    this.updateItemsValuesByColumnType("startDate");
    this.updateItemsValuesByColumnType("endDate");
    this.updateItemsValuesByColumnType("latestStartDate");
    this.updateItemsValuesByColumnType("earliestEndDate");
  }

  setDefaultItemType(value) {
    this.setState({ defaultItemType: value });
    // Update the item values of the type column in did update
  }

  setTwoDigitsToTwoThousandYear(value) {
    this.setState({ twoDigitsToTwoThousandYear: value });
    // Update date column values in did update
  }

  setDateOrder(value) {
    this.setState({ dateOrder: value });
    // Update date column values in did update
  }

  storeMatchCounts(columnType, matched, misMatched) {
    this._storedMatchCounts = this._storedMatchCounts.set(columnType, Map({ matched, misMatched }));
  }

  applyMatchCounts() {
    this.setState({ columnMatchCounts: this.state.columnMatchCounts.merge(this._storedMatchCounts) });
    this._storedMatchCounts = Map();
  }

  updateItemsValuesByColumnType(
    columnType,
    currentColumnTypes = this.state.columnTypes,
    currentColumnInfoOptions = this.state.columnInfoOptions
  ) {
    if (columnType !== CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT) {
      this._itemsValues = clearItemValues(this._itemsValues, columnType);
    }

    const { newValues, newRelValues, matched, misMatched } = updateItemsValues(
      this._itemsValues,
      this._relItemsValues,
      columnType,
      currentColumnTypes,
      getCsvImportDataOnly(this.state.parsedData, this.state.hasHeaderRow),
      this.state.defaultItemType,
      this.props.typesById,
      this.props.referencesById,
      this.props.referenceAllIds,
      this.props.colorsById,
      this.state.dateOrder,
      this.state.twoDigitsToTwoThousandYear,
      this.props.calendar,
      currentColumnInfoOptions,
      this.props.propertiesById,
      this.props.itemsByLabel,
      this.props.tagsByTag,
      this.state.addToNarrative,
      this.props.nearestColorMap
    );
    this._itemsValues = newValues;
    this._relItemsValues = newRelValues;

    // Store the matched and misMatched to apply that to the real state later
    this.storeMatchCounts(columnType, matched, misMatched);
  }

  reParseRelationshipColumns(
    currentColumnTypes = this.state.columnTypes,
    currentColumnInfoOptions = this.state.columnInfoOptions
  ) {
    // clear the new relationship items
    this._relItemsValues = List();
    // relationship by item type
    currentColumnTypes.forEach(ct => {
      if (this.props.typesById.get(ct) || this.props.referencesById.get(ct)) {
        this.updateItemsValuesByColumnType(ct, currentColumnTypes, currentColumnInfoOptions);
      }
    });
    this.updateItemsValuesByColumnType("super", currentColumnTypes, currentColumnInfoOptions);
    this.updateItemsValuesByColumnType("blocking", currentColumnTypes, currentColumnInfoOptions);
  }

  isRelationshipRelatedColumn(columnType) {
    return (
      columnType === "label" ||
      columnType === "blocking" ||
      columnType === "super" ||
      columnType === "internalId" ||
      Boolean(this.props.typesById.get(columnType)) ||
      Boolean(this.props.referencesById.get(columnType))
    );
  }

  setColumnType(index, value) {
    const { columnTypes } = this.state;
    const prevValue = columnTypes.get(index);
    if (prevValue !== value) {
      const newColumnTypes = columnTypes.set(index, value);
      this.setState({ columnTypes: newColumnTypes });

      if (prevValue !== CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT) {
        this._itemsValues = clearItemValues(this._itemsValues, prevValue);
      }

      this.updateItemsValuesByColumnType(value, newColumnTypes);

      // The relationship related columns will re-parse if the changing column type
      // is relationship related
      if (this.isRelationshipRelatedColumn(value) || this.isRelationshipRelatedColumn(prevValue)) {
        this.reParseRelationshipColumns(newColumnTypes);
      }

      this.applyMatchCounts();
    }
  }

  getSelectedColumnCount() {
    return this.state.columnTypes.filter(ct => ct !== CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT).count();
  }

  updateColumnInfoOptions(columnType, newValue) {
    const { columnInfoOptions, columnTypes } = this.state;
    const prevValue = columnInfoOptions.get(columnType);
    if (prevValue !== newValue) {
      const newColumnInfoOptions = columnInfoOptions.set(columnType, newValue);
      // Change the column info options
      this.setState({ columnInfoOptions: newColumnInfoOptions });
      // Update the item values of the changed column
      if (this.isRelationshipRelatedColumn(columnType)) {
        this.reParseRelationshipColumns(columnTypes, newColumnInfoOptions);
      } else {
        this.updateItemsValuesByColumnType(columnType, columnTypes, newColumnInfoOptions);
      }
      this.applyMatchCounts();
    }
  }

  getSummaryValues() {
    const { tagsByTag, itemsByLabel } = this.props;
    let normalItemCountMap = this.props.typesById.map(_t => 0);
    let updateItemCountMap = Map(normalItemCountMap);
    let relItemCountMap = normalItemCountMap.merge();
    let newTagsCount = 0;
    let tagsMap = tagsByTag;

    // Fix the types for items
    this._itemsValues = fixItemTypes(this._itemsValues, this.state.defaultItemType);
    // Normal item from the data row
    this._itemsValues.forEach((i, index) => {
      // Will create if the item has label and (type is valid)
      if (i.get(CAN_IMPORT_FIELD) && !isNil(normalItemCountMap.get(i.get("type")))) {
        const internalId = i.get("internalId");
        const existingMatch = internalId
          ? itemsByLabel.find(il => il.internalId === internalId || il.displayId === internalId)
          : null;
        if (existingMatch) {
          // Existing item
          updateItemCountMap = updateItemCountMap.update(i.get("type"), count => count + 1);
          // Update id
          this._itemsValues = this._itemsValues.setIn([index, "id"], existingMatch.id);
        } else {
          normalItemCountMap = normalItemCountMap.update(i.get("type"), count => count + 1);
        }
      }
      // Tags
      if (this.state.columnInfoOptions.get("tags") && i.get("tags") && i.get("tags").count() > 0) {
        i.get("tags").forEach(t => {
          if (!tagsMap.get(t)) {
            newTagsCount += 1;
            tagsMap = tagsMap.set(t, t);
          }
        });
      }
    });

    // Relationship items
    this._relItemsValues.forEach(i => {
      relItemCountMap = relItemCountMap.update(i.get("type"), count => count + 1);
    });

    return {
      normalItemCounts: normalItemCountMap,
      updateItemCounts: updateItemCountMap,
      relItemCounts: relItemCountMap,
      newTagsCount
    };
  }

  onPreviewFinish() {
    if (!this.state.columnTypes.find(t => t === "label")) {
      // No label column
      dialogs.alertPrompt(interfaceMessages.IMPORT_CSV_LABEL_COLUMN_MUST_BE_SELECTED);
    } else if (this._itemsValues.count() === 0) {
      // No data
      dialogs.alertPrompt(interfaceMessages.IMPORT_CSV_AT_LEAST_ONE_ROW_TO_IMPORT);
    } else {
      // Any other types of error ?
      // Going to next step to show the summary
      this.onNextStep(SUMMARY_PAGE);
    }
  }

  onImport() {
    // Update our state so the UI is updated
    this.setState({ isImporting: true });
    // And setup to perform input after the next display update
    this._performImportOnNextUpdate = true;
  }

  performImport() {
    const importActions = createImportActions(
      this._itemsValues,
      this._relItemsValues,
      this.props.itemsByLabel,
      this.state.columnInfoOptions,
      this.state.addToNarrative,
      this.props.typesById,
      this.props.referencesById,
      this.props.propertiesById,
      this.props.calendar,
      this.props.lastRootIndexForNarrative
    );
    this.props.dispatchBatchActions(importActions);
    // Close the modal
    this.props.onCloseClick();
  }

  onCloseClick() {
    if (this.getSelectedColumnCount() > 0) {
      const msg = "Are you sure you want to close without importing?";
      dialogs.okCancelPrompt(msg, result => {
        if (result) {
          this.props.onCloseClick();
        }
      });
    } else {
      this.props.onCloseClick();
    }
  }

  getPreviewPage() {
    const selectedColumnLabel = `${CSV_IMPORT_PREVIEW_TABLE_LABEL} (${this.getSelectedColumnCount()} of ${this.state.columnTypes.count()} selected)`;
    // TODO: grab the real file name
    const fileName = "";
    const rowCount = getCsvImportDataOnly(this.state.parsedData, this.state.hasHeaderRow).count();
    const importingCountText = `Importing ${rowCount} rows`;

    let showGradientBar = Boolean(rowCount > maxRows);
    const container = this.tableContainerRef.current;
    if (container && rowCount > 2) {
      const tableLabelHeight = scalePixelsToFontSize(27, this.props.globalFontSize);
      if (container.clientHeight < this.state.tableHeight + tableLabelHeight) {
        showGradientBar = true;
      }
    }

    return (
      <Fragment>
        <ModalRow>
          <ModalCheckboxGrid columns={3}>
            <ModalField label={CSV_IMPORT_SETTINGS_LABEL}>
              <FormCheckbox
                label={CSV_IMPORT_HEADER_ROW_CHECK_BOX_LABEL}
                checkboxState={this.state.hasHeaderRow}
                onChange={this.setHasHeaderRow}
                helpTooltip={CSV_IMPORT_HEADER_ROW_TOOLTIP}
              />
              {this.props.isNarrativeAllowed ? (
                <FormCheckbox
                  label={CSV_IMPORT_ADD_TO_NARRATIVE_CHECK_BOX_LABEL}
                  checkboxState={this.state.addToNarrative}
                  onChange={this.setAddToNarrative}
                  helpTooltip={CSV_IMPORT_ADD_TO_NARRATIVE_TOOLTIP}
                />
              ) : null}
            </ModalField>
            <ModalField label={CSV_IMPORT_DEFAULT_ITEM_TYPE_LABEL} helpTooltip={CSV_IMPORT_DEFAULT_ITEM_TYPE_TOOLTIP}>
              <ModalFormDropdown
                options={getTypeOptions(this.props.itemTypes, !this.state.addToNarrative)}
                value={this.state.defaultItemType}
                onChange={this.setDefaultItemType}
              />
              <FormText>{CSV_IMPORT_DEFAULT_ITEM_TYPE_DESCRIPTION}</FormText>
            </ModalField>
            <ModalField label={CSV_IMPORT_DATE_FORMAT_LABEL} helpTooltip={CSV_IMPORT_DATE_FORMAT_TOOLTIP}>
              <ModalFormDropdown
                options={csvImportDateOrderOptions}
                value={this.state.dateOrder}
                onChange={this.setDateOrder}
              />
              <FormCheckbox
                label={CSV_IMPORT_DATE_FORMAT_CHECK_BOX_LABEL}
                checkboxState={this.state.twoDigitsToTwoThousandYear}
                onChange={this.setTwoDigitsToTwoThousandYear}
                helpTooltip={CSV_IMPORT_DATE_FORMAT_CHECK_BOX_TOOLTIP}
              />
            </ModalField>
          </ModalCheckboxGrid>
        </ModalRow>
        <ModalRow>
          <div className="tableContainer" ref={this.tableContainerRef}>
            <ModalRow>
              <ModalField label={selectedColumnLabel}>
                <ImportCsvPreviewTable
                  hasHeaderRow={this.state.hasHeaderRow}
                  data={this.state.parsedData}
                  columnCount={this.state.columnCount}
                  columnTypes={this.state.columnTypes}
                  columnTypeOptions={this.props.embeddedColumnTypeOptions}
                  columnMatchCounts={this.state.columnMatchCounts}
                  onColumnTypeChange={this.setColumnType}
                  columnInfoOptions={this.state.columnInfoOptions}
                  onColumnInfoOptionsChange={this.updateColumnInfoOptions}
                  typesById={this.props.typesById}
                  referencesById={this.props.referencesById}
                  onChangeTableHeight={this.onChangeTableHeight}
                  maxRows={maxRows}
                />
              </ModalField>
            </ModalRow>
            {showGradientBar ? <div className="gradientBar" /> : null}
          </div>
        </ModalRow>

        <ModalRow>
          <ModalField flex={3}>
            <FormText>{fileName}</FormText>
          </ModalField>
          <ModalField flex={1}>
            <FormText alignRight>{importingCountText}</FormText>
          </ModalField>
        </ModalRow>
      </Fragment>
    );
  }

  getSummaryPage() {
    const { typesById } = this.props;
    const { normalItemCounts, updateItemCounts, relItemCounts, newTagsCount } = this.getSummaryValues();

    const loadingMessage =
      getImportItemCount(normalItemCounts) + getImportItemCount(updateItemCounts) + getImportItemCount(relItemCounts) >
      CSV_IMPORT_SHOW_LOADING_ITEM_NUM ? (
          <div className="loadingMessage">If you are importing a large number of rows, this may take several minutes</div>
        ) : null;

    const normalItems = normalItemCounts.reduce((rows, count, typeId) => {
      return count > 0
        ? rows.push(
          getSummaryLine(
            `${count} rows will be imported as new ${getTypeLabel(typeId, typesById)}.`,
            `normal-${typeId}`
          )
        )
        : rows;
    }, List());

    const updateItems = updateItemCounts.reduce((rows, count, typeId) => {
      return count > 0
        ? rows.push(
          getSummaryLine(
            `${count} rows will be imported as updated ${getTypeLabel(typeId, typesById)}.`,
            `normal-${typeId}`
          )
        )
        : rows;
    }, List());

    const relItems = relItemCounts.reduce((rows, count, typeId) => {
      return count > 0
        ? rows.push(
          getSummaryLine(
            `${count} ${getTypeLabel(typeId, typesById)} will be created from added relationships.`,
            `rel-${typeId}`
          )
        )
        : rows;
    }, List());

    const newTags = newTagsCount > 0 ? getSummaryLine(`${newTagsCount} tags will be created from added tags.`) : null;

    return (
      <Fragment>
        <ModalRow>
          <ModalField>{loadingMessage}</ModalField>
        </ModalRow>
        <ModalRow>
          <ModalField>
            <ul>
              {normalItems.toArray()}
              {updateItems.toArray()}
              {relItems.toArray()}
              {newTags}
            </ul>
          </ModalField>
        </ModalRow>
      </Fragment>
    );
  }

  getPageContent(pageId) {
    switch (pageId) {
      case PREVIEW_PAGE:
        return this.getPreviewPage();
      case SUMMARY_PAGE:
        return this.getSummaryPage();
    }
  }

  getRightButtonClick(currentStep) {
    if (currentStep === PREVIEW_PAGE) {
      return this.onPreviewFinish;
    } else if (currentStep === SUMMARY_PAGE) {
      return this.onImport;
    }
  }

  getModalHeading(currentStep) {
    const title = MODAL_TITLE[currentStep];

    return <ModalHeading title={title} showCancel={!this.state.isImporting} onCancelClick={this.onCloseClick} />;
  }

  getModalFooter(currentStep, pageIndex) {
    const showBack = pageIndex > 0;
    const rightButtonLabel = this.state.isImporting
      ? "Importing..."
      : this.state.isImporting
        ? "Loading..."
        : RIGHT_BUTTON_LABEL[currentStep];

    return (
      <ModalFooter>
        <ModalFooterLeftGroup>
          {showBack ? (
            <FormButton primary={false} disabled={this.state.isImporting} label="Back" onClick={this.onBackStep} />
          ) : null}
          {/* <FormButton linkStyle label={CSV_IMPORT_GUIDE_LINK_LABEL} /> */}
        </ModalFooterLeftGroup>
        <ModalFooterRightGroup>
          <ModalFooterLoader visible={this.state.isImporting || this.state.isLoading} skin={this.props.skin} />
          <FormButton
            primary={true}
            disabled={this.state.isImporting || this.state.isLoading}
            label={rightButtonLabel}
            onClick={this.getRightButtonClick(currentStep)}
          />
        </ModalFooterRightGroup>
      </ModalFooter>
    );
  }

  onChangeTableHeight(height) {
    this.setState({
      tableHeight: height
    });
  }

  updateContainerHeight() {
    if (this.props.containerRect && this.props.containerRect.height !== this.state.containerRectHeight) {
      this.setState({ containerRectHeight: this.props.containerRect.height });
    }
  }

  render() {
    const currentStep = this.state.stepHistory.last();
    const pageIndex = STEPS.indexOf(currentStep); // 1 based

    const modalHeading = this.getModalHeading(currentStep, pageIndex);
    const modalBody = this.getPageContent(currentStep);
    const modalFooter = this.getModalFooter(currentStep, pageIndex);

    return (
      // Need a explicit fixedHeight here. Otherwise the slider content will not show.
      <ModalView className="importCsvModal" header={modalHeading} footer={modalFooter} fixedHeight={680}>
        <PageSlider pageIndex={pageIndex} pageId={currentStep} transition={sliderTransitions.DEFAULT}>
          <ModalBody>
            <div className="importCsvBodyWrapper">{modalBody}</div>
          </ModalBody>
        </PageSlider>
      </ModalView>
    );
  }
}

ImportCsvModal.propTypes = {
  skin: PropTypes.string.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  dispatchBatchActions: PropTypes.func.isRequired,
  itemTypes: ImmutablePropTypes.list.isRequired,
  defaultItemType: PropTypes.string.isRequired,
  columnTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  embeddedColumnTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  referencesById: ImmutablePropTypes.map.isRequired,
  referenceAllIds: ImmutablePropTypes.list.isRequired,
  colorsById: ImmutablePropTypes.map.isRequired,
  propertiesById: ImmutablePropTypes.map.isRequired,
  itemsByLabel: ImmutablePropTypes.map.isRequired,
  tagsByTag: ImmutablePropTypes.map.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  isNarrativeAllowed: PropTypes.bool.isRequired,
  lastRootIndexForNarrative: PropTypes.number.isRequired,
  nearestColorMap: PropTypes.func.isRequired,
  globalFontSize: PropTypes.number.isRequired,
  // From withContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

const mapStateToProps = state => {
  return {
    skin: preferencesSelectors.getSkin(state.present),
    itemTypes: typeSelectors.getOrderedTypes(state.present),
    defaultItemType: definitionSelectors.getDefaultSequenceType(state.present),
    columnTypeOptions: selectors.getColumnTypeOptions(state.present),
    embeddedColumnTypeOptions: selectors.getEmbeddedColumnTypeOptions(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    referenceAllIds: referenceSelectors.getReferenceAllIds(state.present),
    colorsById: colorSelectors.getColorsById(state.present),
    propertiesById: propertySelectors.getPropertiesById(state.present),
    itemsByLabel: selectors.getItemsByLabel(state.present),
    calendar: calendarSelectors.getCalendar(state.present),
    tagsByTag: selectors.getTagsByTag(state.present),
    isNarrativeAllowed: definitionSelectors.isNarrativeAllowed(state.present),
    lastRootIndexForNarrative: narrativeSelectors.getLastRootIndexForNarrative(state.present),
    nearestColorMap: colorSelectors.getNearestColorMapFromAllColors(state.present),
    globalFontSize: preferencesSelectors.getGlobalFontSize(state.present)
  };
};

const readonlyActions = {
  onCloseClick: uiStateOperations.hideImportCsvModal
};

const editingActions = {
  dispatchBatchActions: actions => {
    return batchOperations.reBatchActions(actions);
  }
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withContainerContext(ImportCsvModal));
